import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useApiClient } from '../../../utils/apiClient';
import { useApiResource } from '../../../utils/useApiResource';
import { UNREAD_COUNT_POLL_INTERVAL_MS } from '../designTokens';
import type { UnreadCountResponse } from '../types';

export interface UseUnreadCountResult {
  unread: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /**
   * Escape hatch so a read/dismiss response's OWN count updates the badge
   * without a re-poll (SPEC "the count is refreshed from the read/dismiss
   * response rather than re-polled").
   */
  setUnread: (unread: number) => void;
}

/**
 * The bell's badge (AC-27, AC-28, AC-11).
 *
 * Built on {@link useApiResource} rather than a hand-rolled interval, because
 * the property that matters — a failed poll must hold the badge's last known
 * value rather than resetting to zero (SPEC "Edge cases") — is exactly what
 * `useApiResource` already gives for free: its `error` branch never touches
 * `data`, so a `refetch()` that fails leaves the previous count in place. This
 * hook only adds the interval and the `visibilitychange` pause on top.
 *
 * The interval is plain `setInterval`, not another `useApiResource` fetch keyed
 * on a ticking dependency — a ticking dep would create a new subscription every
 * tick rather than reusing one, and would have no way to skip a tick outright
 * while the tab is hidden (an effect still has to run to observe "hidden").
 */
export function useUnreadCount(): UseUnreadCountResult {
  const { data, loading, error, refetch, setData } = useApiResource<UnreadCountResponse>(
    (client) => client.get<UnreadCountResponse>('/notifications/inbox/count'),
    [],
    { errorPrefix: 'Failed to load your unread notification count' },
  );

  const { getAccessToken } = useAuth();
  const apiClient = useApiClient(getAccessToken);

  /**
   * Bumped every time a read/dismiss response writes the badge directly.
   *
   * The poll cannot go through `refetch`, because `useApiResource`'s own
   * staleness guard (`fetchIdRef`) only orders its fetches against EACH OTHER —
   * it knows nothing about {@link setUnread}, which writes straight to the
   * state setter. So a poll in flight when the user hits "Mark all read" would
   * resolve afterwards with the pre-mark count and put the badge back up, on a
   * panel the reader had just cleared. That is the same lie as the badge
   * silently resetting to zero, in the other direction, and the SPEC is
   * explicit that the count comes from the read/dismiss response rather than
   * from a re-poll.
   *
   * So the poll fetches itself and refuses to write if this counter moved while
   * it was in the air.
   */
  const explicitWriteSeqRef = useRef(0);

  /**
   * A failed poll is REPORTED but never written to the count.
   *
   * Reported, because the hook's `error` would otherwise only ever describe the
   * very first load, and a caller asking "is this badge trustworthy right now"
   * deserves a truthful answer. Never written, because a badge that dropped to
   * zero on a network blip would report an absence that is not true (SPEC
   * "Edge cases") — so `data` is left exactly where it was.
   */
  const [pollError, setPollError] = useState<string | null>(null);

  const poll = useCallback(async () => {
    const seenAt = explicitWriteSeqRef.current;
    try {
      const next = await apiClient.get<UnreadCountResponse>('/notifications/inbox/count');
      // Superseded by a read/dismiss response while in flight — that value is
      // both newer and authoritative, so this one is simply dropped.
      if (explicitWriteSeqRef.current !== seenAt) return;
      setPollError(null);
      setData(next);
    } catch (err) {
      setPollError(err instanceof Error ? err.message : 'Failed to refresh your unread count');
    }
  }, [apiClient, setData]);

  // Closed over via a ref so the interval effect below never re-subscribes.
  const pollRef = useRef(poll);
  useEffect(() => {
    pollRef.current = poll;
  }, [poll]);

  useEffect(() => {
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };
    const start = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(() => {
        void pollRef.current();
      }, UNREAD_COUNT_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // A backgrounded tab costs nothing (AC-28) — no more ticks until it
        // is looked at again.
        stop();
        return;
      }
      // Coming back into view is itself a reason to check, rather than
      // waiting out however much of the interval happened to be left when the
      // tab was hidden.
      void pollRef.current();
      start();
    };

    if (document.hidden) {
      stop();
    } else {
      start();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const setUnread = useCallback(
    (unread: number) => {
      explicitWriteSeqRef.current += 1;
      setData({ unread });
    },
    [setData],
  );

  return {
    // `data` is `null` before the first response lands; 0 is the honest
    // "nothing known yet" reading there — the "never silently reset" rule
    // only applies once a real value has been seen (see the hook doc above).
    unread: data?.unread ?? 0,
    loading,
    // The first load's failure leads: with no count ever received there is no
    // last known value to hold, which is the more consequential failure.
    error: error ?? pollError,
    refetch,
    setUnread,
  };
}
